import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as duckdb from "https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@1.28.1-dev106.0/+esm";
import QRCode from "qrcode.react";
import API_URL from "./api.config";

function FilePage({trackEvent}) {
  const { fileKey } = useParams();
  const [db, setDb] = useState(null);
  const [sqlQuery, setSqlQuery] = useState("SELECT * FROM data LIMIT 10");
  const [queryResult, setQueryResult] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fileInfo, setFileInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("english");
  const [englishPrompt, setEnglishPrompt] = useState("Get first 10 rows");
  const [isGeneratingSql, setIsGeneratingSql] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [copyAlert, setCopyAlert] = useState({ show: false, message: '' });

  useEffect(() => {
    const checkBackendAvailability = async () => {
      try {
        await axios.get(`${API_URL}/health`);
        setBackendAvailable(true);
      } catch (error) {
        console.error("Backend is not available:", error);
        setBackendAvailable(false);
      }
    };

    checkBackendAvailability();
  }, []);

  useEffect(() => {
    const initDB = async () => {
      try {
        setIsInitialLoading(true);
        setIsLoading(true);
        
        const timeoutDuration = 10000; // 10 seconds
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

        const response = await axios.get(
          API_URL + `/file/${fileKey}`,
          {
            responseType: "blob",
            signal: controller.signal
          }
        );
        trackEvent('FilePage', 'initDB', fileKey);

        clearTimeout(timeoutId);

        const csvContent = await response.data.text();

        const fileinfoResponse = await axios.get(`${API_URL}/file-info/${fileKey}`);
        
        const newFileInfo = {
          key: `${fileKey}.csv`,
          name: `${fileinfoResponse.data.original_filename}`,
          size: response.data.size,
          schema: fileinfoResponse.data.schema,
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours from now
        };

        setFileInfo(newFileInfo);

        // Initialize DuckDB
        const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
        const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
        const worker_url = URL.createObjectURL(
          new Blob([`importScripts("${bundle.mainWorker}");`], {
            type: "text/javascript",
          })
        );
        const worker = new Worker(worker_url);
        const logger = new duckdb.ConsoleLogger();
        const db = new duckdb.AsyncDuckDB(logger, worker);
        await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
        URL.revokeObjectURL(worker_url);

        const c = await db.connect();
        const csvBuffer = new TextEncoder().encode(csvContent);
        await db.registerFileBuffer("data.csv", csvBuffer);

        await c.query(
          `CREATE TABLE data AS SELECT * FROM read_csv('data.csv', AUTO_DETECT=TRUE)`
        );
        setDb(db);
      } catch (e) {
        console.error("Failed to initialize DuckDB or load CSV", e);
        setError("Failed to initialize DuckDB or load CSV: " + e.message);
      } finally {
        setIsInitialLoading(false);
        setIsLoading(false);
      }
    };

    initDB();
  }, [fileKey]);

  useEffect(() => {
    if (db) {
      handleQuerySubmit();
    }
  }, [db]);

  if (!backendAvailable) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service Unavailable</h1>
          <p className="text-gray-600">We're sorry, but the service is currently unavailable. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleQuerySubmit = async () => {
    trackEvent('FilePage', 'handleQuerySubmit', fileKey);
    if (!db || !sqlQuery) return;

    try {
      setIsLoading(true);
      
      // Check if backend is available
      const isBackendAvailable = await checkBackendAvailability();
      if (!isBackendAvailable) {
        setBackendAvailable(false);
        return;
      }

      const conn = await db.connect();
      const result = await conn.query(sqlQuery);
      setQueryResult(result);
      await conn.close();
      setError("");
    } catch (error) {
      console.error("Error running query:", error);
      setError("Error running query: " + error.message);
      setQueryResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const checkBackendAvailability = async () => {
    try {
      await axios.get(`${API_URL}/health`);
      return true;
    } catch (error) {
      console.error("Backend is not available:", error);
      return false;
    }
  };

  const handleGenerateSql = async () => {
    trackEvent('FilePage', 'handleGenerateSql', fileKey);
    if (!englishPrompt) return;

     // Check if backend is available
     const isBackendAvailable = await checkBackendAvailability();
     if (!isBackendAvailable) {
       setBackendAvailable(false);
       return;
     }

    try {
      setIsGeneratingSql(true);
      const response = await axios.post(`${API_URL}/generate-sql`, {
        prompt: englishPrompt,
        schema: fileInfo.schema
      });
      setSqlQuery(response.data.sql);
      setActiveTab("sql");
    } catch (error) {
      console.error("Error generating SQL:", error);
      setError("Error generating SQL: " + error.message);
    } finally {
      setIsGeneratingSql(false);
    }
  };

  const handleDownload = () => {
    trackEvent('FilePage', 'handleDownload', fileKey);
    window.open(API_URL +  `/file/${fileKey}`, "_blank");
  };

  const handleDownloadQueryResults = () => {
    trackEvent('FilePage', 'handleDownloadQueryResults', fileKey);
    if (!queryResult || !queryResult.schema || !queryResult.toArray) return;

    const columns = queryResult.schema.fields.map(field => field.name);
    const rows = queryResult.toArray();

    let csvContent = columns.join(',') + '\n';
    rows.forEach(row => {
      csvContent += columns.map(column => formatValue(row[column])).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "query_results.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const shareableLink = `${window.location.origin}/file/${fileKey}`;

  const copyToClipboard = () => {
    trackEvent('FilePage', 'copyToClipboard', fileKey);
    navigator.clipboard.writeText(shareableLink).then(() => {
      setCopyAlert({ show: true, message: 'Link copied to clipboard!' });
      setTimeout(() => {
        setCopyAlert({ show: false, message: '' });
      }, 2000);
    });
  };

  const formatValue = (value) => {
    if (value instanceof Date) {
      return value.toLocaleString();
    }
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
    return value !== null && value !== undefined ? value.toString() : '';
  };

  const renderQueryResult = () => {
    if (!queryResult || !queryResult.schema || !queryResult.toArray) {
      return <p>No results to display.</p>;
    }

    const columns = queryResult.schema.fields.map(field => field.name);
    const rows = queryResult.toArray();

    return (
      <div className="overflow-x-auto bg-card rounded-lg shadow">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {columns.map((column, index) => (
                <th key={index} className="border-b border-gray-300 p-2 text-left">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {columns.map((column, cellIndex) => (
                  <td key={cellIndex} className="border-b border-gray-300 p-2">
                    {formatValue(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSchema = () => {
    if (!fileInfo || !fileInfo.schema) return null;

    return (
      <div className="overflow-x-auto bg-card rounded-lg shadow">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-gray-300 p-2 text-left">Column Name</th>
              <th className="border-b border-gray-300 p-2 text-left">Data Type</th>
            </tr>
          </thead>
          <tbody>
            {fileInfo.schema.map((column, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="border-b border-gray-300 p-2">{column.name}</td>
                <td className="border-b border-gray-300 p-2">{column.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const Alert = ({ message }) => (
    <div className="fixed top-4 inset-x-0 flex justify-center items-start z-50">
      <div className="bg-card text-text border border-border px-6 py-3 rounded-lg shadow-lg text-center animate-in fade-in slide-in-from-top-4 duration-300">
        <p>{message}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-1/3">
          <div className="bg-card rounded-lg p-6 mb-8">
            {fileInfo && (
              <div className="mb-4 text-text text-sm">
                <h1 className="text-pretty break-words text-3xl font-semibold mb-4 text-text">{fileInfo.name}</h1>
                <p className="text-secondary">Size: {(fileInfo.size / 1024).toFixed(2)} KB</p>
              </div>
            )}
            <button
              onClick={handleDownload}
              disabled={isLoading}
              className="w-full mb-4 py-2 px-4 bg-primary text-white rounded-md hover:bg-blue-600 transition duration-150 text-sm flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download File
            </button>
          </div>

          {copyAlert.show && <Alert message={copyAlert.message} />}

          <div className="bg-card rounded-lg p-6 mb-8">
            <h2 className="text-xl font-md mb-4 text-text">Share File</h2>
            <div className="flex items-center justify-between mb-4">
              <input
                type="text"
                value={shareableLink}
                readOnly
                className="flex-grow mr-2 p-2 border border-gray-300 rounded-md text-sm text-text"
              />
              <button
                onClick={copyToClipboard}
                className="p-2 text-primary hover:text-blue-600 transition duration-150"
                aria-label="Copy link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            <div className="flex justify-center">
              <QRCode value={shareableLink} size={128} />
            </div>
          </div>

          {fileInfo && fileInfo.schema && (
            <div className="bg-card rounded-lg p-6 mb-8">
              <h2 className="text-xl font-md mb-4 text-text">File Schema</h2>
              {renderSchema()}
            </div>
          )}
        </div>

        <div className="w-full md:w-2/3">
          <div className="bg-card rounded-lg p-6 mb-8">
            <h2 className="text-xl font-md mb-4 text-text">Query Data</h2>
            <div className="mb-4">
              <div className="flex border-b border-gray-200">
                <button
                  className={`py-2 px-4 ${activeTab === 'english' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('english')}
                >
                  English
                </button>
                <button
                  className={`py-2 px-4 ${activeTab === 'sql' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('sql')}
                >
                  SQL
                </button>
              </div>
            </div>
            {activeTab === 'english' ? (
              <div>
                <textarea
                  value={englishPrompt}
                  onChange={(e) => setEnglishPrompt(e.target.value)}
                  placeholder="Describe your query in English"
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4 text-sm text-text"
                  disabled={isGeneratingSql}
                />
                <button
                  onClick={handleGenerateSql}
                  disabled={isGeneratingSql || !englishPrompt}
                  className={`py-2 px-4 rounded-md text-white font-semibold transition duration-150 text-sm ${
                    isGeneratingSql || !englishPrompt ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-blue-600'
                  }`}
                >
                  Generate SQL
                </button>
              </div>
            ) : (
              <div>
                <textarea
                  value={sqlQuery}
                  onChange={(e) => setSqlQuery(e.target.value)}
                  placeholder="Enter your SQL query here"
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4 text-sm text-text"
                  disabled={isLoading}
                />
                <button
                  onClick={handleQuerySubmit}
                  disabled={isLoading || !sqlQuery}
                  className={`py-2 px-4 rounded-md text-white font-semibold transition duration-150 text-sm ${
                    isLoading || !sqlQuery ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-blue-600'
                  }`}
                >
                  Run Query
                </button>
              </div>
            )}
          </div>
          {isLoading && <p className="text-text text-sm">Loading...</p>}
          {error && <p className="text-red-600 mb-4 text-sm">{error}</p>}
          {queryResult && (
            <div className="bg-card rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-md text-text">Query Result:</h3>
                <button
                  onClick={handleDownloadQueryResults}
                  className="flex items-center py-2 px-4 bg-secondary text-white rounded-md hover:bg-blue-600 transition duration-150 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Results
                </button>
              </div>
              {renderQueryResult()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FilePage;